/**
 * A paste the dialog's paste places take themselves (Option C uxfix review UXF-01). Letting the browser insert the text
 * and reading it back in `onChange` put the whole string — a password in it included — into the input's value, where any
 * `input` / `beforeinput` listener and the native undo stack could read it, before the chip replaced it. Instead the text
 * is read from the clipboard event and the insertion cancelled: the caller keeps it in state, so a connection string goes
 * straight to the chip and never enters the input. Anything else (a filter term) is shown as the paste would have shown
 * it — at the selection, line breaks dropped, the caret after it (`usePasteCaret`). A text dropped on the input is taken
 * the same way (`takeDrop`, uxfix review r2): it lands at the selection, not at the drop point, and the input is focused.
 */
import { useLayoutEffect, useRef, type ClipboardEvent, type DragEvent, type RefObject, type SyntheticEvent } from 'react';

/** `value`: the input's text with the paste inserted at the selection; `pasted`: the pasted text alone. */
export type TakenPaste = { value: string; caret: number; pasted: string };

/** The input's value after the paste, the insertion cancelled; null when the clipboard holds no text (nothing happens). */
export function takePaste(event: ClipboardEvent<HTMLInputElement>): TakenPaste | null {
  return take(event, event.clipboardData?.getData('text') ?? '');
}

/** The same for a text dropped on the input (the browser's insertion cancelled); null when the drop carries no text. */
export function takeDrop(event: DragEvent<HTMLInputElement>): TakenPaste | null {
  const taken = take(event, event.dataTransfer?.getData('text') ?? '');
  if (taken) event.currentTarget.focus();
  return taken;
}

function take(event: SyntheticEvent<HTMLInputElement>, text: string): TakenPaste | null {
  if (!text) return null;
  event.preventDefault();
  const input = event.currentTarget;
  // An input drops line breaks from a paste.
  const pasted = text.replace(/[\r\n]+/gu, '');
  const start = input.selectionStart ?? input.value.length;
  const end = input.selectionEnd ?? start;
  return { value: input.value.slice(0, start) + pasted + input.value.slice(end), caret: start + pasted.length, pasted };
}

/**
 * Puts the caret after a taken paste once its value is rendered (a controlled value update moves it to the end). Only on
 * the render that shows that value: a chip shows none of it, and a later keystroke never inherits a stale caret.
 */
export function usePasteCaret(inputRef: RefObject<HTMLInputElement>): (paste: TakenPaste) => void {
  const pending = useRef<TakenPaste | null>(null);
  useLayoutEffect(() => {
    const paste = pending.current;
    pending.current = null;
    const input = inputRef.current;
    if (paste && input && input.value === paste.value) input.setSelectionRange(paste.caret, paste.caret);
  });
  return (paste: TakenPaste) => {
    pending.current = paste;
  };
}
